import React, { useState } from 'react';
import CalmiCharacter from '../pet/components/CalmiCharacter';
import { DAILY_PHRASE, CALMI_SUPPORT } from '../../shared/constants';
import { Card, NavigationBar, ThemedScreen } from '../../shared/components';
import { AVATARS } from '../../shared/constants/avatars';
import { BACKGROUND_THEMES } from '../../shared/constants/backgroundThemes';
import { useAvatar } from '../../shared/hooks/useAvatar';
import { useBackgroundTheme } from '../../shared/hooks/useBackgroundTheme';

const HomeScreen: React.FC<{ userName: string; onNavigate: (screen: string) => void }> = ({ 
  userName, 
  onNavigate 
}) => {
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const { selectedAvatar, setSelectedAvatar, selectedAvatarData } = useAvatar();
  const { selectedTheme, setSelectedTheme, selectedThemeData } = useBackgroundTheme();

  const handleAvatarSelect = (avatarId: typeof selectedAvatar) => {
    setSelectedAvatar(avatarId);
  };

  const handleThemeSelect = (themeId: typeof selectedTheme) => {
    setSelectedTheme(themeId);
  };
  const actions: Array<{ id: string; label: string; icon: string; color: string; borderColor: string }> = [
    { id: 'chat', label: 'Hablar con Calmi', icon: '🌸', color: 'from-blue-50 to-blue-100', borderColor: 'border-blue-200' },
    { id: 'play', label: 'Jugar y Crear', icon: '🎨', color: 'from-purple-50 to-purple-100', borderColor: 'border-purple-200' },
    { id: 'diary', label: 'Mi diario', icon: '📖', color: 'from-pink-50 to-pink-100', borderColor: 'border-pink-200' },
  ];

  return (
    <ThemedScreen className="p-4 sm:p-6 md:p-8">
      <div className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold">¡Hola, {userName}! ✨</h1>
            <button
              onClick={() => setShowAvatarSelector(!showAvatarSelector)}
              className="p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-md hover:shadow-lg transition-all border border-slate-200"
              title="Cambiar avatar"
            >
              <span className="text-2xl">{selectedAvatarData.emoji}</span>
            </button>
          </div>
          <p className="text-sm sm:text-base opacity-80 mt-1">¿Cómo te sientes hoy?</p>
        </div>

        {/* Avatar & Theme Selector */}
        {showAvatarSelector && (
          <Card className="mb-6 p-6 bg-white/95 backdrop-blur-sm border-2 border-blue-200 shadow-xl animate-bounce-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">🎭 Elige tu mascota y fondo</h3>
              <button
                onClick={() => setShowAvatarSelector(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-all"
              >
                <span className="text-xl">✕</span>
              </button>
            </div>

            <h4 className="text-sm font-semibold text-slate-600 mb-3">Mascota</h4>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {AVATARS.map((avatar) => (
                <button
                  key={avatar.id}
                  onClick={() => handleAvatarSelect(avatar.id)}
                  className={`p-4 rounded-2xl border-2 transition-all ${
                    selectedAvatar === avatar.id
                      ? 'border-blue-500 bg-blue-50 scale-105 shadow-md'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:scale-105'
                  }`}
                >
                  <div className="text-4xl mb-2">{avatar.emoji}</div>
                  <p className="text-sm text-gray-700 font-medium">{avatar.name}</p>
                  {selectedAvatar === avatar.id && (
                    <div className="text-xs text-blue-600 mt-1 font-semibold">✓</div>
                  )}
                </button>
              ))}
            </div>

            <h4 className="text-sm font-semibold text-slate-600 mb-3">Fondo de Calmi</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {BACKGROUND_THEMES.map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => handleThemeSelect(theme.id)}
                  className={`p-3 rounded-2xl border-2 transition-all text-left relative overflow-hidden ${
                    selectedTheme === theme.id
                      ? 'border-blue-500 scale-105 shadow-md'
                      : 'border-gray-200 hover:border-blue-300 hover:scale-105'
                  }`}
                  style={{
                    backgroundImage: `${theme.pattern}, ${theme.gradient}`,
                    backgroundSize: `${theme.patternSize}, 100% 100%`
                  }}
                >
                  <div className={`text-4xl mb-2 drop-shadow-md ${theme.textColor}`}>{theme.emoji}</div>
                  <p className={`text-sm font-semibold drop-shadow-sm ${theme.textColor}`}>{theme.name}</p>
                  {selectedTheme === theme.id && (
                    <div className="absolute top-2 right-2 text-xs bg-white text-blue-600 rounded-full w-5 h-5 flex items-center justify-center font-bold">✓</div>
                  )}
                </button>
              ))}
            </div>
          </Card>
        )}

        {/* Calmi Character */}
        <div className="flex justify-center mb-6">
          <div className="animate-subtle-float">
            <CalmiCharacter size="large" className="sm:size-medium" />
          </div>
        </div>

        {/* Speech Bubble */}
        <Card className="mb-6 relative bg-white/90 backdrop-blur-sm border border-slate-200 shadow-lg">
          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white rotate-45 border-l border-t border-slate-200"></div>
          <p className="text-sm sm:text-base text-slate-700 text-center font-medium leading-relaxed">
            {CALMI_SUPPORT}
          </p>
        </Card>

        {/* Frase del día */}
        <Card className="mb-6 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 shadow-md">
          <h2 className="text-base sm:text-lg md:text-xl font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <span className="text-lg sm:text-xl">🌟</span> Frase del día
          </h2>
          <p className="text-sm sm:text-base text-slate-700 italic leading-relaxed">
            "{DAILY_PHRASE}"
          </p>
        </Card>

        {/* ¿Qué necesitas hoy? */}
        <div className="mb-6 pb-20">
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-slate-800 mb-5">
            ¿Qué necesitas hoy?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {actions.map((action) => (
              <button
                key={action.id}
                onClick={() => onNavigate(action.id)}
                className={`p-4 sm:p-5 md:p-6 rounded-2xl bg-gradient-to-br ${action.color} border-2 ${action.borderColor} hover:shadow-lg transition-all btn-interactive transform hover:-translate-y-1`}
              >
                <div className="text-3xl sm:text-4xl md:text-5xl mb-2 animate-subtle-float">{action.icon}</div>
                <div className="text-sm sm:text-base md:text-lg text-slate-800 font-semibold">{action.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Bottom Navigation */}
        <NavigationBar activeItem="home" onNavigate={onNavigate} />
      </div>
    </ThemedScreen>
  );
};

export default HomeScreen;
